import React, { useState } from "react";
import Button from "../../../../components/form/Button";
import Table from "../../../../components/elements/table/Table";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { eventCountList } from "../../../../services/eventService";
import { toast } from "react-toastify";
import { orderList } from "../../../../services/orderService";
import { useDebounce } from "../../../../hooks/useDebounce";
import { lngDetect } from "../../../../helpers/lngDetect";
import dayjs from "dayjs";
import Tooltip from "../../../../components/elements/Tooltip";
import StatdataItem from "../../../../components/items/StatCardItem";
import Payouts from "../../../../components/sections/front/user-events-collections/Payouts";
import Recommendations from "./Recommendations";
import { ticketList } from "../../../../services/ticketService";
import { useOrganization } from "../../../../context/OrganizationContext";
import { ShareButtons, useClipboard } from "../../../../util/Flag";
import { organizationTeamList } from "../../../../services/organizationService";
import ManageOrderDrawer from "../../../../components/sections/front/Order/Drawer/ManageOrderDrawer";
import { t } from "i18next";

const EventDashboard = () => {
  const navigate = useNavigate();
  const { session } = useSelector((state) => state?.authProvider);
  const { selectedOrganization } = useOrganization();
  const { selectedOrganizationDetails } = useSelector(
    (state) => state?.organization
  );
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const { event, eventLoading, selectedLanguage } = useOutletContext();
  const [orderkeyword, setorderrKeyword] = useState("");
  const ordersearch = useDebounce(orderkeyword, 300);
  const [orderFilters, setOrderFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const [eventCountFilters, setEventCountFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const { copied, copy } = useClipboard();
  const user_role = selectedOrganizationDetails?.data?.verificationData?.role;
  const promoter_url =
    user_role === "promoter"
      ? `/${selectedLanguage}/event-details/${event?.data?.uid}?referral_code=${session?.user?.uid}`
      : null;

  const data = `${window.location?.origin}/${selectedLanguage}/event-details/${
    event?.data?.uid
  }${user_role === "promoter" ? `?referral_code=${session?.user?.uid}` : ""}`;

  // Beautiful Status Badge Component - Enhanced Version
  const StatusBadge = ({ status }) => {
    const getStatusConfig = (status) => {
      const configs = {
        active: {
          label: "Active",
          bgColor: "bg-emerald-50",
          textColor: "text-emerald-700",
          borderColor: "border-emerald-400",
        },
        invited: {
          label: "Invited",
          bgColor: "bg-blue-50",
          textColor: "text-blue-700",
          borderColor: "border-blue-400",
        },
        suspended: {
          label: "Suspended",
          bgColor: "bg-red-50",
          textColor: "text-red-700",
          borderColor: "border-red-400",
        },
        pending: {
          label: "Pending",
          bgColor: "bg-amber-50",
          textColor: "text-amber-700",
          borderColor: "border-amber-400",
        },
        accepted: {
          label: "Accepted",
          bgColor: "bg-green-50",
          textColor: "text-green-700",
          borderColor: "border-green-400",
        },
      };

      return (
        configs[status?.toLowerCase()] || {
          label: status || "Unknown",
          bgColor: "bg-gray-50",
          textColor: "text-gray-700",
          borderColor: "border-gray-400",
        }
      );
    };

    const config = getStatusConfig(status);

    return (
      <div
        className={`inline-flex items-center gap-2.5 px-3 py-1 rounded-full border transition-all duration-300 hover:shadow-lg ${config.bgColor} ${config.borderColor} ${config.glowColor}`}
      >
        <span
          className={`text-xs font-medium ${config.textColor} capitalize tracking-wide`}
        >
          {config.label}
        </span>
      </div>
    );
  };

  const { data: teamData } = useQuery({
    queryKey: ["team-list-dashboard", filters, event?.data?._id],
    enabled: !!event?.data?._id,
    queryFn: async () => {
      const res = await organizationTeamList({
        ...filters,
        organization: selectedOrganization,
        type: "promoter-invitation",
        event: event?.data?._id,
      });
      return res?.docs;
    },
  });

  const { data: ticketData } = useQuery({
    queryKey: ["ticket", filters, event?.data?._id],
    queryFn: async () => {
      const res = await ticketList({
        ...filters,
        organization: selectedOrganization,
        event: event?.data?._id,
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!event?.data?._id,
  });

  const tablePromoterHeadData = [
    {
      _id: "Name",
      name: t("Name"),
      align: "left",
      isSort: false,
      isFilter: false,
    },
    {
      _id: "referral_code",
      name: t("referral code"),
      align: "left",
      isSort: false,
      isFilter: false,
    },
    {
      _id: "Email",
      name: t("Email"),
      align: "left",
      isSort: false,
      isFilter: false,
    },
    {
      _id: "status",
      name: t("status"),
      align: "left",
      isSort: false,
      isFilter: false,
    },
  ];

  const tablePromoterData = teamData?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: "name",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] !max-w-none !w-[10%]",
          data: item?.info?.displayName || "-",
        },
        {
          _id: "code",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] !max-w-none !w-[10%]",
          data: item?.uid ? (
            <Tooltip
              messageClass={"!px-3 !py-2 !text-xm"}
              message={copied ? "Copied!" : "Copy"}
            >
              <button
                type="button"
                onClick={() => copy(item?.uid)}
                className="rounded-md py-1 lg:py-2 px-2 h-10 lg:h-9 text-center bg-primary/10 border border-primary text-xs font-medium flex-1"
              >
                {item?.uid}
              </button>
            </Tooltip>
          ) : (
            "-"
          ),
        },
        {
          _id: "email",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] !max-w-none !w-[10%]",
          data: item?.userData?.email || "-",
        },
        {
          _id: "status",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] !max-w-none !w-[10%]",
          data: item?.status ? <StatusBadge status={item?.status} /> : "-",
        },
      ]?.filter(Boolean),
    };
  });

  const tableHeadTicketData = [
    {
      _id: "ticket_name",
      name: t("Ticket Name"),
      align: "left",
      type: "text",
      isSort: false,
      isFilter: false,
    },
    {
      _id: "ticket_type",
      name: t("Ticket type"),
      align: "left",
      type: "text",
      isSort: false,
      isFilter: false,
    },
    {
      _id: "sold",
      name: t("Sold"),
      align: "left",
      type: "text",
      isSort: false,
      isFilter: false,
    },
    {
      _id: "price",
      name: t("Price"),
      align: "right",
      type: "text",
      isSort: false,
      isFilter: false,
    },
  ];

  const tableTicketData = ticketData?.data?.map((ticket, index) => {
    return {
      _id: ticket?._id.toString(),
      rowData: [
        {
          type: "text",
          data: lngDetect(ticket.name),
          align: "left",
        },
        {
          type: "text",
          data: ticket.type,
          align: "left",
        },
        {
          type: "text",
          data: `${ticket?.quantitySold || 0}/${ticket?.quantity || 0}`,
          align: "left",
        },
        {
          type: "text",
          data: `${
            ticket?.eventData?.currency?.symbol || "$"
          } ${ticket.price?.toFixed(2)}`,
          align: "right",
        },
      ],
    };
  });

  const {
    data: orderData,
    isLoading: orderLoading,
    isFetching: orderFetching,
    refetch,
  } = useQuery({
    queryKey: ["order", orderFilters, ordersearch, event?.data?._id],
    queryFn: async () => {
      const res = await orderList({
        ...orderFilters,
        organization: selectedOrganization,
        event: event?.data?._id,
        ...(ordersearch && { keyword: ordersearch }),
        viewAs: "seller",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!event?.data?._id,
  });

  const {
    data: eventCount,
    isLoading: eventCountLoading,
    isFetching: eventCountFetching,
  } = useQuery({
    queryKey: ["eventCountData", eventCountFilters, event?.data?._id],
    queryFn: async () => {
      const res = await eventCountList({
        ...eventCountFilters,
        event: event?.data?._id,
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.data;
    },
    enabled: !!event?.data?._id,
  });

  const orderTableHeadData = [
    { _id: "order", name: t("Order"), align: "left", type: "text" },
    { _id: "eventName", name: t("Event Name"), align: "left", type: "text" },
    // { _id: "quantity", name: "Quantity", align: "left", type: "text" },
    // { _id: "ticketType", name: "Ticket type", align: "left", type: "text" },
    { _id: "price", name: t("Price"), align: "right", type: "text" },
    { _id: "date", name: t("Date"), align: "center", type: "text" },
  ];

  const orderTableData = orderData?.data?.map((order, index) => {
    return {
      _id: order?._id.toString(),
      rowData: [
        {
          type: "text",
          data: (
            <button className="text-deep-purple transition-colors font-medium">
              {order?.orderId}
            </button>
          ),
          align: "left",
        },
        {
          type: "text",
          data: lngDetect(order?.eventData?.title),
          align: "left",
        },
        // {
        //   type: "text",
        //   data: order?.quantity,
        //   align: "left",
        // },
        // {
        //   type: "text",
        //   data: order?.ticketType,
        //   align: "left",
        // },
        {
          type: "text",
          data: (
            <span>
              {order?.eventData?.currency?.symbol || "$"} {order?.total || 0}
            </span>
          ),
          align: "right",
        },
        {
          type: "text",
          data: dayjs(order?.createdAt).format("DD MMM YYYY, hh:mm A"),
          align: "right",
        },
      ],
      onClick: (id) => {
        setModalOpen((prev) => ({ ...prev, isOpen: true, data: { _id: id } }));
      },
    };
  });

  if (!event?.data?._id && !eventLoading) {
    navigate(`/${selectedLanguage}/user/events`);
    return;
  }

  const statsCards = [
    {
      id: "net-sales",
      // icon: "fa-light fa-dollar-sign",
      iconStr: event?.data?.currency?.symbol || "$",
      title: t("Net Sales"),
      value: eventCount?.netSales.toFixed(2),
      description: `Open event sales report →`,
      link: `/${selectedLanguage}/user/myEvent/report/order-sales/${event?.data?.uid}`,
      showButton: true,
    },
    {
      id: "tickets-sold",
      icon: "fa-light fa-ticket-alt",
      title: t("Tickets Sold"),
      value: eventCount?.ticketsSold,
      subValue: eventCount?.ticketsAvailable,
      description: `${t("Open ticket sales report")} →`,
      link: `/${selectedLanguage}/user/myEvent/report/tickets-sales/${event?.data?.uid}?ticketType=ticket`,
      showButton: true,
      progress: "9.5%",
    },
    {
      id: "addons-sold",
      icon: "fa-light fa-plus-circle",
      title: t("Add-ons sold"),
      value: eventCount?.addonsSold,
      subValue: eventCount?.addonsAvailable,
      description: `${t("Open add-on sales report")} →`,
      link: `/${selectedLanguage}/user/myEvent/report/tickets-sales/${event?.data?.uid}?ticketType=add_ons`,
      showButton: true,
    },
    {
      id: "page-views",
      icon: "fa-light fa-eye",
      title: t("Page Views"),
      value: eventCount?.pageViews,
      // description: "Open page views report →",
      showButton: true,
    },
  ];

  const available = {
    label: t("Available for payout"),
    amount: "USD 0.00",
    note: t("Waiting for more ticket sales"),
  };

  const recommendations = [
    {
      title: t("Make it easier to sell tickets at the door"),
      description: t(
        "and check in attendees quickly with the dancebrite Organizer app."
      ),
      button: {
        label: t("Get the app"),
        icon: "fa fa-download",
      },
    },
  ];

  function orderManageResponse({ res, status }) {
    if (res?.status === 200 || res?.status === 201) {
      refetch();
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  return (
    <div className="bg-white py-4 px-4 rounded-md">
      <div className="absolute inset-0">
        {/* <div className="absolute top-20 left-20 w-72 h-72 bg-blue-100/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-96 h-96 bg-purple-100/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-40 w-80 h-80 bg-pink-100/30 rounded-full blur-3xl animate-pulse delay-2000"></div> */}
      </div>

      <div className="relative z-10">
        <div className="w-full mx-auto">
          <div className="group relative shadow rounded-lg cursor-pointer transition-all duration-300 border border-transparent flex flex-col px-5 py-5 h-full mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="space-y-4 w-full">
                <div className="flex justify-between">
                  <div className="flex flex-wrap items-center gap-4 flex-shrink">
                    <div className="w-12 md:w-16 aspect-square bg-primary/10 rounded-xl flex items-center justify-center text-primary text-3xl">
                      <i className="fa-light fa-calendar-star"></i>
                    </div>
                    <div>
                      <h1 className="text-xl lg:text-2xl font-bold text-deep-purple font-Montserrat">
                        {t("Event Dashboard")}
                      </h1>
                      {/* <div className="flex items-center text-primary text-sm">
                        <i className="fas fa-clock mr-2"></i>
                        <span>Jul 11, 2025 at 10:00am</span>
                      </div> */}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <ShareButtons url={data} text="Join this amazing event:-" />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 bg-gray-50 rounded-2xl p-0 lg:p-4">
                  <span className="text-sm text-deep-purple font-medium w-full lg:w-auto">
                    {t("Event link")}
                  </span>
                  <Tooltip message={data} position="bottom">
                    <div className="flex items-center bg-primary/10 rounded-md px-4 py-2 text-sm max-w-md border border-primary break-all">
                      <span
                        className="text-primary font-medium truncate underline cursor-pointer max-w-60 md:max-w-xl"
                        onClick={() => {
                          navigate(
                            user_role === "promoter"
                              ? promoter_url
                              : `/${selectedLanguage}/event-details/${event?.data?.uid}`
                          );
                        }}
                      >
                        {data}
                      </span>
                    </div>
                  </Tooltip>

                  <div className="relative">
                    <Tooltip
                      messageClass={"!px-3 !py-2 !text-xm"}
                      message={copied ? "Copied!" : "Copy"}
                    >
                      <Button
                        buttonLabelClasses="text-deep-purple"
                        buttonIcon="fa fa-copy"
                        buttonIconPosition="left"
                        buttonFunction={() => copy(data)}
                        buttonClasses="bg-primary/10 text-primary px-0  px-2 border-primary rounded-md h-10"
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 mb-8">
            {statsCards.map((card) => (
              <StatdataItem
                key={card.id}
                data={card}
                loading={eventCountLoading}
              />
            ))}
          </div>

          <div className="grid grid-cols-12 gap-8 items-start">
            <div className="col-span-12 lg:col-span-6 xl:col-span-7">
              <Payouts eventCount={eventCount} />
            </div>
            <div className="col-span-12 lg:col-span-6 xl:col-span-5 sticky top-28">
              <Recommendations
                title={t("Recommendations")}
                badge="1 of 1"
                items={recommendations}
              />
            </div>
          </div>

          <div className="group relative shadow rounded-lg transition-all duration-300 border border-transparent flex flex-col px-5 py-5 h-full mb-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-shrink-0 flex items-center justify-center w-12 md:w-16 aspect-square rounded-lg bg-primary/10 text-primary text-3xl">
                <i className="fa-light fa-chart-bar"></i>
              </div>
              <h2 className="text-xl lg:text-2xl font-bold text-deep-purple font-Montserrat">
                {t("Sales by ticket type")}
              </h2>
            </div>
            <Table
              tableHeadData={tableHeadTicketData}
              tableData={tableTicketData}
              isLoder={false}
              pageCount={1}
              currentPage={1}
              list={[]}
              setOriginalData={() => {}}
              containerClasses=""
              tableClasses="border border-gray-100"
              isOrderingDisabled={true}
            />

            <div className="relative flex">
              <Button
                buttonLabel={t("Go to the Sales by ticket type report")}
                buttonLabelClasses={
                  "text-deep-purple text-sm md:text-base capitalize "
                }
                buttonHasLink
                buttonLink={`/${selectedLanguage}/user/myEvent/report/tickets-sales/${event?.data?.uid}`}
                buttonIconPosition="right"
                buttonIconRight={"fa fa-arrow-right"}
                buttonClasses={"px-0 bg-transparent text-deep-purple"}
              />
            </div>
          </div>

          <div className="group relative shadow rounded-lg cursor-pointer transition-all duration-300 border border-transparent flex flex-col px-5 py-5 h-full mb-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-shrink-0 flex items-center justify-center w-12 md:w-16 aspect-square rounded-lg bg-primary/10 text-primary text-3xl">
                <i className="fa-light fa-receipt"></i>
              </div>
              <h2 className="text-xl lg:text-2xl font-bold text-deep-purple font-Montserrat">
                {t("Recent Orders")}
              </h2>
            </div>
            <Table
              tableHeadData={orderTableHeadData}
              tableData={orderTableData}
              isLoder={false}
              pageCount={1}
              currentPage={1}
              isOrderingDisabled={true}
              containerClasses=""
              tableClasses="border border-gray-100"
            />

            <ManageOrderDrawer
              open={modalOpen?.isOpen}
              data={modalOpen?.data}
              close={() => setModalOpen({ isOpen: false, data: null })}
              getResponse={orderManageResponse}
            />

            <div className="relative flex">
              <Button
                buttonLabel={t("Go to all Orders")}
                buttonLabelClasses={
                  "text-text-deep-purple text-base capitalize"
                }
                buttonHasLink
                buttonLink={`/${selectedLanguage}/user/myEvent/order/${event?.data?.uid}/all`}
                buttonIconPosition="right"
                buttonIconRight={"fa-regular fa-arrow-right"}
                buttonClasses={"px-0 bg-transparent text-deep-purple"}
              />
            </div>
          </div>

          <div className="group relative shadow rounded-lg transition-all duration-300 border border-transparent flex flex-col px-5 py-5 h-full mb-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-shrink-0 flex items-center justify-center w-12 md:w-16 aspect-square rounded-lg bg-primary/10 text-primary text-3xl">
                <i className="fa-light fa-receipt"></i>
              </div>
              <h2 className="text-xl lg:text-2xl font-bold text-deep-purple font-Montserrat">
                {t("Recent Promoters")}
              </h2>
            </div>
            <Table
              tableHeadData={tablePromoterHeadData}
              tableData={tablePromoterData}
              isLoder={false}
              pageCount={1}
              currentPage={1}
              isOrderingDisabled={true}
              containerClasses=""
              tableClasses="border border-gray-100"
            />

            <div className="relative flex">
              <Button
                buttonLabel={t("Go to all Promoters")}
                buttonLabelClasses={
                  "text-text-deep-purple text-base capitalize"
                }
                buttonHasLink
                buttonLink={`/${selectedLanguage}/user/promoters/${event?.data?.uid}`}
                buttonIconPosition="right"
                buttonIconRight={"fa-regular fa-arrow-right"}
                buttonClasses={"px-0 bg-transparent text-deep-purple"}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDashboard;
